import React, { useState } from 'react';
import { X, Check, CreditCard, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ContactForm } from './payment/ContactForm';
import { PaymentForm } from './payment/PaymentForm';

interface OrganizationPlansDialogProps {
  onClose: () => void;
  onSelectPlan?: (plan: string) => void;
}

export const OrganizationPlansDialog: React.FC<OrganizationPlansDialogProps> = ({ 
  onClose,
  onSelectPlan 
}) => {
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [showContactForm, setShowContactForm] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const navigate = useNavigate();

  const plans = [
    {
      name: 'Kostenlos',
      price: '0 €',
      period: 'pro Monat',
      value: 'free',
      features: [
        '1 Benutzer',
        '3 Gebäude',
        'Basis-Funktionen',
        'Lokale Datenspeicherung',
        'Community Support'
      ]
    },
    {
      name: 'Starter',
      price: '9,9 €',
      period: 'pro Monat',
      value: 'starter',
      features: [
        '5 Benutzer',
        '10 Gebäude',
        'Erweiterte Funktionen',
        'API-Zugriff',
        'E-Mail Support',
        'Microsoft Teams Integration',
        '10 GB Speicher',
        'Tägliche Backups'
      ]
    },
    {
      name: 'Professional',
      price: '14,9 €',
      period: 'pro Monat',
      value: 'professional',
      features: [
        '20 Benutzer',
        'Unbegrenzt Gebäude',
        'Alle Funktionen',
        'Voller API-Zugriff',
        'Priority Support',
        'Vollständige Microsoft 365 Integration',
        '100 GB Speicher',
        'Stündliche Backups',
        'Power BI Integration',
        'Anpassbare Workflows'
      ]
    },
    {
      name: 'Enterprise',
      price: 'Individuell',
      period: '',
      value: 'enterprise',
      features: [
        'Unbegrenzt Benutzer',
        'Unbegrenzt Gebäude',
        'Alle Funktionen',
        'Dedizierter Account Manager',
        '24/7 Premium Support',
        'Vollständige Microsoft 365 Integration',
        'Unbegrenzter Speicher',
        'Echtzeit-Backups',
        'Power BI Integration',
        'Vollständig anpassbar',
        'On-Premise Option',
        'SLA Garantie'
      ]
    }
  ];

  const handleSelectPlan = (plan: string) => {
    setSelectedPlan(plan);
    
    if (plan === 'enterprise') {
      setShowContactForm(true);
    } else if (plan !== 'free') {
      setShowPaymentForm(true);
    } else {
      // For free plan, just continue with registration
      if (onSelectPlan) {
        onSelectPlan(plan);
      }
      onClose();
    }
  };

  const handlePaymentComplete = () => {
    if (selectedPlan && onSelectPlan) {
      onSelectPlan(selectedPlan);
    }
    onClose();
  };

  const handleContactSubmit = () => {
    if (selectedPlan && onSelectPlan) {
      onSelectPlan(selectedPlan);
    }
    setShowContactForm(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        {!showPaymentForm && !showContactForm ? (
          <>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Preise und Leistungen</h2>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {plans.map((plan) => (
                <div
                  key={plan.name}
                  className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                    <div className="flex items-baseline mb-6">
                      <span className="text-3xl font-bold text-blue-600">{plan.price}</span>
                      {plan.period && (
                        <span className="ml-2 text-gray-500">{plan.period}</span>
                      )}
                    </div>

                    <div className="space-y-4">
                      <div className="space-y-2">
                        {plan.features.map((feature, index) => (
                          <div key={index} className="flex items-start">
                            <Check className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                            <span className="ml-2 text-gray-600">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="p-6 bg-gray-50 border-t border-gray-100">
                    <button
                      onClick={() => handleSelectPlan(plan.value)}
                      className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                    >
                      {plan.name === 'Enterprise' ? 'Kontakt aufnehmen' : 'Jetzt starten'}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 text-center text-sm text-gray-500">
              Alle Preise zzgl. MwSt. Änderungen vorbehalten.
            </div>
          </>
        ) : showContactForm ? (
          <ContactForm 
            onSubmit={handleContactSubmit} 
            onCancel={() => {
              setShowContactForm(false);
              setSelectedPlan(null);
            }}
          />
        ) : (
          <PaymentForm 
            plan={selectedPlan || 'starter'} 
            onComplete={handlePaymentComplete}
            onCancel={() => {
              setShowPaymentForm(false);
              setSelectedPlan(null);
            }}
          />
        )}
      </div>
    </div>
  );
};